// 性能优化工具函数

// 防抖函数
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// 节流函数
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// 批量请求处理器
export class BatchProcessor<T, R> {
  private batch: T[] = [];
  private timeoutId: NodeJS.Timeout | null = null;
  private readonly batchSize: number;
  private readonly batchTimeout: number;
  private readonly processor: (items: T[]) => Promise<R[]>;

  constructor(
    processor: (items: T[]) => Promise<R[]>,
    batchSize = 10,
    batchTimeout = 100
  ) {
    this.processor = processor;
    this.batchSize = batchSize;
    this.batchTimeout = batchTimeout;
  }

  add(item: T): Promise<R> {
    return new Promise((resolve, reject) => {
      this.batch.push(item);
      
      const itemIndex = this.batch.length - 1;
      
      // 如果达到批量大小，立即处理
      if (this.batch.length >= this.batchSize) {
        this.processBatch().then(results => {
          resolve(results[itemIndex]);
        }).catch(reject);
      } else {
        // 否则设置超时处理
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
        }
        
        this.timeoutId = setTimeout(() => {
          this.processBatch().then(results => {
            resolve(results[itemIndex]);
          }).catch(reject);
        }, this.batchTimeout);
      }
    });
  }

  private async processBatch(): Promise<R[]> {
    if (this.batch.length === 0) return [];
    
    const currentBatch = [...this.batch];
    this.batch = [];
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    return await this.processor(currentBatch);
  }
}

// 简单的LRU缓存
export class LRUCache<K, V> {
  private cache = new Map<K, V>();
  private readonly maxSize: number;

  constructor(maxSize: number = 100) {
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    if (this.cache.has(key)) {
      // 移到最后（最近使用）
      const value = this.cache.get(key)!;
      this.cache.delete(key);
      this.cache.set(key, value);
      return value;
    }
    return undefined;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // 删除最少使用的（第一个）
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  delete(key: K): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }
}

// 请求合并器 - 合并相同的请求
export class RequestDeduplicator<T> {
  private pendingRequests = new Map<string, Promise<T>>();

  async request(key: string, requestFn: () => Promise<T>): Promise<T> {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }
}

// 性能监控器
export class PerformanceMonitor {
  private static metrics = new Map<string, number[]>();
  private static coreWebVitals = {
    lcp: [] as number[],
    fid: [] as number[],
    cls: [] as number[],
    fcp: [] as number[],
    ttfb: [] as number[]
  };

  static startTimer(label: string): () => void {
    const start = performance.now();
    
    return () => {
      const duration = performance.now() - start;
      
      if (!this.metrics.has(label)) {
        this.metrics.set(label, []);
      }
      
      this.metrics.get(label)!.push(duration);
      
      // 保持最近100个测量值
      const measurements = this.metrics.get(label)!;
      if (measurements.length > 100) {
        measurements.shift();
      }
      
      console.log(`⏱️ ${label}: ${duration.toFixed(2)}ms`);
    };
  }

  static getStats(label: string) {
    const measurements = this.metrics.get(label) || [];
    if (measurements.length === 0) {
      return null;
    }

    const avg = measurements.reduce((a, b) => a + b, 0) / measurements.length;
    const min = Math.min(...measurements);
    const max = Math.max(...measurements);

    return { avg, min, max, count: measurements.length };
  }

  static getAllStats() {
    const stats: Record<string, any> = {};
    this.metrics.forEach((measurements, label) => {
      stats[label] = this.getStats(label);
    });
    return stats;
  }

  // Core Web Vitals监控
  static initCoreWebVitals() {
    if (typeof window === 'undefined') return;

    // LCP (Largest Contentful Paint)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      const lcp = lastEntry.startTime;
      this.coreWebVitals.lcp.push(lcp);
      console.log(`📊 LCP: ${lcp.toFixed(2)}ms`);
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

    // FID (First Input Delay)
    const fidObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry: any) => {
        const fid = entry.processingStart - entry.startTime;
        this.coreWebVitals.fid.push(fid);
        console.log(`📊 FID: ${fid.toFixed(2)}ms`);
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });

    // CLS (Cumulative Layout Shift)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
          this.coreWebVitals.cls.push(clsValue);
          console.log(`📊 CLS: ${clsValue.toFixed(4)}`);
        }
      });
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });

    // FCP (First Contentful Paint)
    const fcpObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        this.coreWebVitals.fcp.push(entry.startTime);
        console.log(`📊 FCP: ${entry.startTime.toFixed(2)}ms`);
      });
    });
    fcpObserver.observe({ entryTypes: ['paint'] });

    // TTFB (Time to First Byte)
    if ('performance' in window && 'timing' in window.performance) {
      const timing = window.performance.timing;
      const ttfb = timing.responseStart - timing.navigationStart;
      this.coreWebVitals.ttfb.push(ttfb);
      console.log(`📊 TTFB: ${ttfb}ms`);
    }

    // 暴露到全局作用域，方便在浏览器控制台中访问
    if (typeof window !== 'undefined') {
      (window as any).PerformanceMonitor = PerformanceMonitor;
      console.log('🎯 PerformanceMonitor 已暴露到全局作用域，可在控制台中使用 window.PerformanceMonitor 访问');
    }
  }

  static getCoreWebVitals() {
    const getMetricStats = (values: number[]) => {
      if (values.length === 0) return null;
      return {
        value: values[values.length - 1],
        avg: values.reduce((a, b) => a + b, 0) / values.length,
        min: Math.min(...values),
        max: Math.max(...values),
        count: values.length
      };
    };

    return {
      lcp: getMetricStats(this.coreWebVitals.lcp),
      fid: getMetricStats(this.coreWebVitals.fid),
      cls: getMetricStats(this.coreWebVitals.cls),
      fcp: getMetricStats(this.coreWebVitals.fcp),
      ttfb: getMetricStats(this.coreWebVitals.ttfb)
    };
  }
} 